import {
    getDatePartsInTimezone,
    getTimeInTimezone,
    getTimezoneOffset,
    getVendorTimezoneFromPage,
    getWallClockInTimezone,
} from '@/utils/timezone.js';
import { currentLocale } from '@/composables/useLocale.js';

export const fullDate = {
    methods: {
        fullDate(date) {
            const timezone = getVendorTimezoneFromPage(this.$page);
            const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: timezone };
            var d = new Date(date).toLocaleDateString(currentLocale(), dateOptions);
            d = d.charAt(0).toUpperCase() + d.slice(1);
            d = d.replace('den', 'd.');
            return d;
        },
    },
};

export const shortDateWithTime = {
    methods: {
        shortDate(date) {
            const timezone = getVendorTimezoneFromPage(this.$page);
            const thisDate = new Date(date);
            const m = thisDate.toLocaleDateString(currentLocale(), { month: 'short', timeZone: timezone });

            return getDatePartsInTimezone(thisDate, timezone).d + '. ' + m;
        },
    },
};

function localAndPrettyDateFunction(date, type = '', vendorTimezone = null) {
    // Use vendor timezone if provided, otherwise fall back to Europe/Copenhagen
    const timezone = vendorTimezone ?? 'Europe/Copenhagen';
    const dateOptions = { year: 'numeric', month: 'short', day: 'numeric', timeZone: timezone };
    const timeOptions = { hour: '2-digit', minute: '2-digit', timeZone: timezone };
    if (date !== null) {
        let localDate = new Date(date).toLocaleDateString(currentLocale(), dateOptions);
        let localTime = new Date(date).toLocaleTimeString(currentLocale(), timeOptions);
        localTime.replace('.', ':');
        if (type === '') {
            return localDate + ', ' + localTime;
        } else if (type === 'time-only') {
            return localTime;
        } else if (type === 'date-only') {
            return localDate;
        }
    } else {
        return null;
    }
}

export const localAndPrettyDate = {
    methods: {
        localAndPrettyDate(date, type = '') {
            return localAndPrettyDateFunction(date, type, getVendorTimezoneFromPage(this.$page));
        },
    },
};

export const prettyDurationInfo = {
    computed: {
        prettyDurationInfo() {
            let accommodationEnabled = false;
            let search = this.$store.getters.getSearch;
            let eventType = this.$store.getters.getEventType;
            const vendorTimezone = getVendorTimezoneFromPage(this.$page);
            if (search.startTime !== null && search.endTime !== null && search.participants !== null) {
                let info =
                    this.$t('Your selection') +
                    ': ' +
                    localAndPrettyDateFunction(search.startTime, '', vendorTimezone) +
                    ' - ' +
                    localAndPrettyDateFunction(search.endTime, '', vendorTimezone) +
                    ', ' +
                    search.participants +
                    ' ' +
                    this.$t('delegates');
                if (eventType.accommodation_active && accommodationEnabled) {
                    info +=
                        ', ' +
                        (search.accommodation ? this.$t('with accommodation') : this.$t('without accommodation'));
                }
                return info;
            } else {
                return this.$t('You have not selected a date and duration yet');
            }
        },
    },
};

export function quantityCleaner(quantity, min_value = null, max_value = null) {
    let modifiedQuantity = parseInt(quantity);
    if (isNaN(modifiedQuantity)) {
        modifiedQuantity = min_value === null ? 0 : min_value;
    }
    if (min_value !== null && modifiedQuantity < min_value) {
        modifiedQuantity = min_value;
    }
    if (max_value !== null && modifiedQuantity > max_value) {
        modifiedQuantity = max_value;
    }
    return modifiedQuantity;
}

export const createTimesMixin = {
    methods: {
        createTimes(min, max, timesPerHour = 1) {
            let times = [];
            let modifiedMax = max;
            if (max > 24) {
                modifiedMax = 24;
            }
            for (let h = min; h <= modifiedMax; h++) {
                for (let i = 0; i < 60; i += 60 / timesPerHour) {
                    if (h === max && i !== 0) {
                        continue;
                    }

                    // Super hacky 23:59 solution
                    if (h === 24) {
                        h = 23;
                        i = 59;
                    }

                    times.push({
                        displayName: (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i,
                        h: h,
                        i: i,
                        searchValues: [
                            (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i,
                            (h < 10 ? '0' : '') + h + '' + (i < 10 ? '0' : '') + i,
                            (h < 10 ? '0' : '') + h + '.' + (i < 10 ? '0' : '') + i,
                        ],
                        value: (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i + ':00',
                    });

                    // Super hacky but stops loop
                    if (h === 23 && i === 59) {
                        h = 24;
                        i = 1;
                    }
                }
            }
            return times;
        },
    },
};

export const createDaysMixin = {
    methods: {
        createDays(amount) {
            let options = [];
            for (let i = 0; i < amount; i++) {
                options.push({
                    value: i + 1,
                    displayName: this.$t('Day') + ' ' + (i + 1),
                });
            }
            return options;
        },
    },
};

export const cbDateTimesMixin = {
    methods: {
        cbDateObject(date) {
            return getDatePartsInTimezone(date, getVendorTimezoneFromPage(this.$page));
        },
        cbTimeObject(date) {
            const timezone = getVendorTimezoneFromPage(this.$page);
            const { hour, minute } = getWallClockInTimezone(date, timezone);

            return {
                h: hour,
                i: minute,
                displayName: getTimeInTimezone(date, timezone),
            };
        },
        cbDateTimeObjectCombine(cbDateObject, cbTimeObject) {
            return {
                y: cbDateObject.y,
                m: cbDateObject.m,
                d: cbDateObject.d,
                h: cbTimeObject.h,
                i: cbTimeObject.i,
            };
        },
        cbDateObjectToUTC(cbDateTimeObject) {
            const timezone = this.$page?.props?.vendor?.timezone ?? 'Europe/Copenhagen';
            const offset = getTimezoneOffset(timezone, cbDateTimeObject);
            // Subtract offset to convert from local time to UTC
            // e.g., 09:00 Copenhagen (UTC+1) = 08:00 UTC, so 9 - 1 = 8
            return new Date(
                Date.UTC(
                    cbDateTimeObject.y,
                    cbDateTimeObject.m,
                    cbDateTimeObject.d,
                    cbDateTimeObject.h - offset,
                    cbDateTimeObject.i ? cbDateTimeObject.i : 0
                )
            ).toISOString();
        },
    },
};
